import { Card } from "primereact/card";
import "./profile-sidebar.scss";
import { Skeleton } from "primereact/skeleton";
import ReactCountryFlag from "react-country-flag";
import { Link } from "react-router-dom";
import CustomeButton from "../Button";

interface ProfileSidebarProps {
  profileDetails: ProfileDetails;
  title: string;
  isLoading: boolean;
  activeTab: string;
  tabs: ProfileTabs[];
  entityLink?: {
    name: string;
    url: string;
  };
  isActive?: boolean;
  showCountryFlag?: boolean;
  hasDeleteBtn?: boolean;
  showContactInfo?: boolean;
  deleteBtnHanlder?: () => void;
}
interface ProfileDetails {
  given_name?: string;
  surname?: string;
  other_name?: string;
  preferred_name?: string;
  country: string;
  email: string;
  phone_code?: string;
  phone_number?: string;
}

interface ProfileTabs {
  label: string;
  link: string;
  code: string;
}

const ProfileSidebar = ({
  profileDetails,
  title,
  isLoading,
  activeTab,
  tabs,
  entityLink,
  isActive,
  showCountryFlag = false,
  hasDeleteBtn = false,
  showContactInfo = true,
  deleteBtnHanlder,
}: ProfileSidebarProps) => {
  const getInitials = () => {
    const firstInitial =
      profileDetails && profileDetails?.given_name
        ? profileDetails.given_name[0].charAt(0)
        : "";
    const lastInitial =
      profileDetails && profileDetails?.surname
        ? profileDetails.surname[0].charAt(0)
        : "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };
  return (
    <Card className="profile-sidebar-container flex p-0">
      <div className="flex flex-column align-items-center gap-4">
        {/* Profile photo section */}
        <div className="avatar-container">
          <h3> {getInitials()} </h3>
        </div>

        {/* About section */}
        <div className="about-section flex flex-column w-full align-items-center gap-2">
          {isLoading ? (
            <Skeleton width="150px" height="20px" className="mr-1" />
          ) : (
            <p className="profile-name m-0">
              {profileDetails?.given_name}{" "}
              {profileDetails?.other_name ? profileDetails?.other_name : null}{" "}
              {profileDetails?.surname}
            </p>
          )}
          {isLoading ? (
            <Skeleton width="150px" height="20px" className="mr-1" />
          ) : (
            <p className="title m-0"> {title} </p>
          )}

          {/* Entity Link */}
          {entityLink &&
            (isLoading ? (
              <Skeleton width="150px" height="20px" className="mr-1" />
            ) : (
              <Link to={entityLink.url} className="entity-link">
                {showCountryFlag && (
                  <ReactCountryFlag
                    countryCode={profileDetails?.country}
                    svg
                    className="mr-1"
                  />
                )}
                {entityLink.name}
              </Link>
            ))}

          {/* Active Status */}
          {isActive !== undefined &&
            (isLoading ? (
              <Skeleton width="80px" height="20px" className="mr-1" />
            ) : (
              <div
                className={`status-badge ${isActive ? "active" : "inactive"}`}
              >
                <span className="status-dot"></span>
                {isActive ? "Active" : "Not Active"}
              </div>
            ))}
        </div>

        {/* Contact section  */}
        <div className="flex flex-column w-full gap-2 align-items-center">
          {isLoading ? (
            <Skeleton width="150px" height="20px" className="mr-1" />
          ) : (
            showContactInfo && (
              <p className="contact-info flex align-items-center gap-3 m-0">
                <i className="pi pi-envelope" />
                {profileDetails.email}
              </p>
            )
          )}
          {isLoading ? (
            <Skeleton width="150px" height="20px" className="mr-1" />
          ) : (
            showContactInfo &&
            (profileDetails.phone_code || profileDetails.phone_number) && (
              <p className="contact-info flex align-items-center gap-3 m-0">
                <i className="pi pi-phone" />
                {profileDetails.phone_code && `(${profileDetails.phone_code})`}
                {profileDetails.phone_number && profileDetails.phone_number}
              </p>
            )
          )}

          {/* Delete button */}
          {hasDeleteBtn && (
            <CustomeButton
              label="Delete Profile"
              icon="pi pi-trash"
              iconPos="right"
              variant="outlined"
              className="profile-delete-btn"
              onClick={deleteBtnHanlder}
            />
          )}
        </div>
        {/* Tabs section */}
        <div className="flex flex-column gap-2 w-full">
          {tabs.map((tab) => {
            return (
              <Link
                to={tab.link}
                key={tab.code}
                className={`sidebar-align ${activeTab === tab.code ? "active-tab" : "menu-hover"}`}
              >
                <p className="sidebar-menu"> {tab.label} </p>
                <span className="pi pi-angle-right icon-color"></span>
              </Link>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default ProfileSidebar;
